import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Building2,
  CalendarDays,
  MessageSquare,
  DollarSign,
  Settings,
  ChartBar,
  LogOut,
  Bot,
} from "lucide-react";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

const menuItems = [
  {
    title: "Dashboard",
    path: "/host",
    icon: <ChartBar className="h-4 w-4" />,
  },
  {
    title: "Properties",
    path: "/host/properties",
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    title: "Bookings",
    path: "/host/bookings",
    icon: <CalendarDays className="h-4 w-4" />,
  },
  {
    title: "Messages",
    path: "/host/messages",
    icon: <MessageSquare className="h-4 w-4" />,
  },
  {
    title: "Earnings",
    path: "/host/earnings",
    icon: <DollarSign className="h-4 w-4" />,
  },
  {
    title: "AI Assistant",
    path: "/host/ai-assistant",
    icon: <Bot className="h-4 w-4" />,
  },
  {
    title: "Settings",
    path: "/host/settings",
    icon: <Settings className="h-4 w-4" />,
  },
];

export function HostSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Signed out successfully",
        description: "You have been logged out of your account",
      });
      
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: "There was a problem signing you out. Please try again.",
      });
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    // If we're on mobile, try to close the sheet by clicking the close button
    if (isMobile) {
      const closeButton = document.querySelector('[aria-label="Close"]');
      if (closeButton instanceof HTMLButtonElement) {
        closeButton.click();
      }
    }
  };

  return (
    <Sidebar className="border-r border-sidebar-border bg-sidebar transition-all duration-300 ease-in-out">
      <SidebarHeader className="p-4">
        <SidebarMenuButton
          onClick={() => handleNavigation("/")}
          className="w-full text-primary hover:text-primary hover:bg-primary/10 transition-colors duration-200"
          tooltip="Go to Homepage"
        >
          <Home className="h-4 w-4" />
          <span className="font-medium">Home</span>
        </SidebarMenuButton>
      </SidebarHeader>
      
      <SidebarContent className="px-2">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground px-2 py-2">
            Menu
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem
                  key={item.path}
                  className={cn(
                    "mb-1 transition-all duration-200 ease-in-out",
                    location.pathname === item.path 
                      ? "bg-primary/10 text-primary" 
                      : "hover:bg-primary/5"
                  )}
                  onClick={() => handleNavigation(item.path)}
                >
                  <div className="flex items-center space-x-3 px-3 py-2 rounded-md">
                    <div className={cn(
                      "transition-transform duration-200",
                      location.pathname === item.path ? "scale-110" : ""
                    )}>
                      {item.icon}
                    </div>
                    <span className="font-medium">{item.title}</span>
                  </div>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="border-t border-sidebar-border p-4 mt-auto">
        <SidebarMenuButton
          onClick={handleSignOut}
          className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
          tooltip="Sign Out"
        >
          <LogOut className="h-4 w-4" />
          <span className="font-medium">Sign Out</span>
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>
  );
}